import { Button, Flex, IconFont } from '@byecode/ui'
import { DevicePreviewEnum, type DTFile, type FieldProtocol, FILE_MODE, FileConfig, type FileValue } from '@lighthouse/core'
import { nanoid } from '@lighthouse/tools'
import { map } from 'rambda'
import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'

import { getAcceptByFileConfig, getUploadLengthLimitByFileConfig } from '../../../../../constants'
import { useApplicationContext, useLanguageContext } from '../../../../../contexts'
import type { WithStatusFile } from '../../../../../types'
import { getFileNameByUrl, getFileSizeByUrl, getFileTypeByUrl } from '../../../../../utils/helper'
import { TriggerNodeFnProps, WithoutPopperFileUploader } from '../../../../FileUploader'
import type { FieldBaseProps } from '../../../types'
import { UploadBox } from '../UploadBox'

interface FileDrawerFieldProps extends FieldBaseProps {
    value: FileValue
    fileField?: FieldProtocol
    fileConfig: FileConfig
}

const SCxContainer = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    font-size: var(--font-size-normal);
`

export const FileDrawerField: React.FunctionComponent<FileDrawerFieldProps> = props => {
    const { value: data, fileField, config, fileConfig, readOnly, onChange } = props
    const { file } = fileConfig
    const mode = file?.mode
    const { uploadyOptions, breakPoint } = config
    const fileDesign = breakPoint?.fileDesign
    const { isMobile } = useApplicationContext()
    const { convertTextByLanguage: t } = useLanguageContext()
    const gridConfig = useMemo(() => {
        if (mode !== FILE_MODE.grid) {
            return undefined
        }
        return {
            gap: fileDesign?.gap,
            borderRadius: fileDesign?.borderRadius,
            itemWidth: fileDesign?.itemWidth,
            maxCount: fileDesign?.maxCount
        }
    }, [fileDesign, mode])
    const enableItemActions = useMemo(
        () => ({
            canDelete: !readOnly,
            canDownload: fileField?.type !== 'video',
            canPreview: true,
            canRename: !readOnly
        }),
        [fileField?.type, readOnly]
    )

    const usedValue: WithStatusFile[] = useMemo(
        () =>
            map(url => {
                const name = getFileNameByUrl(url) || ''
                const type = getFileTypeByUrl(url)
                const size = getFileSizeByUrl(url)
                return { name, type, size, url, value: url, status: 'success', uid: nanoid(12) }
            }, data),
        [data]
    )

    const fileValue = useMemo(() => usedValue.map(item => item.url), [usedValue])

    const effectiveConfig = file
    const isAvatarField = (fileField?.id ?? '').toUpperCase() === 'AVATAR'
    const isVideoField = fileField?.type === 'video'

    const handleChange = useCallback(
        (files: DTFile[]) => {
            onChange?.({ value: files, type: 'file' })
        },
        [onChange]
    )

    const { max: configuredMaxFiles, min: configuredMinFiles } = useMemo(() => getUploadLengthLimitByFileConfig(file), [file])
    const maxFiles = isAvatarField ? 1 : configuredMaxFiles
    const minFiles = isAvatarField ? 1 : configuredMinFiles
    const accept = useMemo(() => {
        if (isAvatarField) {
            return 'image/*'
        }
        if (isVideoField) {
            return 'video/mp4'
        }
        return getAcceptByFileConfig(effectiveConfig, 'file')
    }, [effectiveConfig, isAvatarField, isVideoField])
    const isOnlyOneFile = useMemo(() => maxFiles === minFiles && minFiles === 1, [maxFiles, minFiles])

    const triggerNodeFn = useCallback(
        (innerFiles: WithStatusFile[], disabled: boolean | undefined, handlers: TriggerNodeFnProps) => {
            if (mode === 'thumbnail') {
                return (
                    <UploadBox
                        title={t('clickOrDragToUpload')}
                        innerFiles={innerFiles}
                        onlyOne={isOnlyOneFile}
                        onPreview={handlers.onPreview}
                        onRemove={handlers.onRemove}
                    />
                )
            }
            if (mode === FILE_MODE.grid) {
                const resolvedItemWidth = gridConfig?.itemWidth
                    ? `${gridConfig.itemWidth.size}${gridConfig.itemWidth.unit || 'px'}`
                    : '80px'
                const borderRadius = gridConfig?.borderRadius != null ? `${gridConfig.borderRadius}px` : '8px'
                return (
                    <div
                        data-field-input
                        data-field-active={false}
                        data-field-disabled={!!disabled}
                        style={{
                            width: resolvedItemWidth,
                            height: resolvedItemWidth,
                            borderRadius,
                            background: 'var(--color-gray-100)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: disabled ? 'not-allowed' : 'pointer',
                            flexShrink: 0
                        }}
                    >
                        <IconFont type="Add" size={32} color="var(--color-gray-400)" />
                    </div>
                )
            }
            return (
                <Button
                    size="lg"
                    data-field-input
                    data-field-active={false}
                    data-field-disabled={!!disabled}
                    icon={
                        <Flex data-field-icon>
                            <IconFont type="CloudArrowUp" size={16} />
                        </Flex>
                    }
                >
                    {t('clickToUpload')} {maxFiles ? `(${innerFiles.length}/${maxFiles})` : ''}
                </Button>
            )
        },
        [gridConfig, isOnlyOneFile, maxFiles, mode, t]
    )

    return (
        <>
            <SCxContainer>
                {uploadyOptions && (
                    <WithoutPopperFileUploader
                        previewType={isMobile ? DevicePreviewEnum.MOBILE : DevicePreviewEnum.DESKTOP}
                        uploadyOptions={uploadyOptions}
                        sortable={false}
                        accept={accept}
                        limit={maxFiles}
                        triggerNodeFn={triggerNodeFn}
                        enableItemActions={enableItemActions}
                        disableUpload={readOnly}
                        hiddenFileList={mode === 'thumbnail' && isOnlyOneFile}
                        onChange={handleChange}
                        uploadFileType={fileField?.type === 'video' ? 'video' : 'file'}
                        files={fileValue}
                        gridConfig={gridConfig}
                    />
                )}
            </SCxContainer>
        </>
    )
}
